// pages/TravelExpense/TravelExpenseDetail.js
//
// Shows one ExpenseApproval (trip) with its expense breakdown and the
// approval log. Manager users see Approve / Reject buttons.
//
// Backend:
//   GET  /api/TravelExpense/{id}
//   POST /api/TravelExpense/{id}/approve   { comment }
//   POST /api/TravelExpense/{id}/reject    { comment }

import React, { useEffect, useState } from "react";
import { Container, Card, CardBody, Row, Col, Button, Input, Label,
    Spinner, Badge, Alert } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardPageHeader } from "../../Components/Common/DashboardKit";
import api from "../../api";
import useAuth from "../Personal Attendance/useAuth";

const fmtDate = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleDateString(); } catch { return "—"; } };
const fmtDt   = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleString();    } catch { return "—"; } };
const fmtMoney = (v) => {
    const n = typeof v === "number" ? v : parseFloat(v);
    if (!isFinite(n)) return "—";
    return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
};
const StatusBadge = ({ status }) => {
    const map = { 1: ["Draft", "secondary"], 2: ["Submitted", "warning"], 3: ["Approved", "success"], 4: ["Denied", "danger"] };
    const [label, color] = map[status] || [`Status ${status}`, "info"];
    return <Badge color={color} pill>{label}</Badge>;
};

const TravelExpenseDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const auth = useAuth();
    const canApprove = auth.isManager || auth.isItAdmin || auth.isSuperUser;

    const [trip, setTrip] = useState(null);
    const [log, setLog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [comment, setComment] = useState("");
    const [busy, setBusy] = useState(false);

    const reload = () => {
        setLoading(true); setError(null);
        api.get(`TravelExpense/${id}`)
            .then(({ data }) => { setTrip(data?.Trip ?? null); setLog(Array.isArray(data?.Log) ? data.Log : []); })
            .catch((e) => setError(e?.response?.data?.error || e?.message || "Failed to load"))
            .finally(() => setLoading(false));
    };
    useEffect(reload, [id]);  // eslint-disable-line

    const act = async (kind) => {
        setBusy(true); setError(null);
        try {
            await api.post(`TravelExpense/${id}/${kind}`, { comment });
            setComment("");
            reload();
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || `${kind} failed`);
        } finally {
            setBusy(false);
        }
    };

    if (loading) return (<div className="page-content"><Container fluid><div className="text-center py-5"><Spinner color="primary" /></div></Container></div>);
    if (error)   return (<div className="page-content"><Container fluid><Alert color="danger">{error}</Alert></Container></div>);
    if (!trip)   return (<div className="page-content"><Container fluid><Alert color="warning">Trip not found.</Alert></Container></div>);

    // Status 2 = Submitted (awaiting approval). Only show Approve/Reject
    // buttons when the trip is in that state.
    const isApprovable = canApprove && trip.status === 2;

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    title={trip.nameOfTrip ?? `Trip #${trip.id}`}
                    breadcrumb={[{ label: "Travel Expense", to: "/workflows?wf=travel" }, { label: trip.nameOfTrip ?? `Trip #${trip.id}` }]}
                    subtitle={<>
                        <StatusBadge status={trip.status} />{" "}
                        <span className="text-muted">submitted {fmtDate(trip.submitDate)}</span>
                    </>}
                    breadcrumbItems={[
                        { label: "Travel Expense", href: "/travel-expense" },
                        { label: `Trip #${trip.id}` },
                    ]}
                />

                <Row className="g-3 mt-1">
                    <Col xl={8}>
                        <Card>
                            <CardBody>
                                <h5 className="mb-3">Trip details</h5>
                                <Row>
                                    <Col md={6}><div className="text-muted small">Primary Objective</div><div>{trip.primaryObjective || "—"}</div></Col>
                                    <Col md={3}><div className="text-muted small">Leave Home</div><div>{fmtDate(trip.leaveHomeDate)}</div></Col>
                                    <Col md={3}><div className="text-muted small">Return Home</div><div>{fmtDate(trip.returnHomeDate)}</div></Col>
                                </Row>

                                <h6 className="mt-4 mb-2">Estimated Expenses</h6>
                                <table className="table table-sm mb-0">
                                    <tbody>
                                        <tr><th>Airfare</th>        <td className="text-end">{fmtMoney(trip.airfare)}</td></tr>
                                        <tr><th>Lodging</th>        <td className="text-end">{fmtMoney(trip.lodging)}</td></tr>
                                        <tr><th>Meals</th>          <td className="text-end">{fmtMoney(trip.meals)}</td></tr>
                                        <tr><th>Entertainment</th>  <td className="text-end">{fmtMoney(trip.entertainment)}</td></tr>
                                        <tr><th>Car Rental</th>     <td className="text-end">{fmtMoney(trip.carRental)}</td></tr>
                                        <tr><th>Personal Car</th>   <td className="text-end">{fmtMoney(trip.personalCar)}</td></tr>
                                        <tr><th>Taxi</th>           <td className="text-end">{fmtMoney(trip.taxi)}</td></tr>
                                        <tr><th>Other</th>          <td className="text-end">{fmtMoney(trip.other)}</td></tr>
                                        <tr className="table-light"><th>Total</th>
                                            <td className="text-end fw-bold">
                                                {fmtMoney((trip.airfare ?? 0) + (trip.lodging ?? 0) + (trip.meals ?? 0)
                                                    + (trip.entertainment ?? 0) + (trip.carRental ?? 0)
                                                    + (trip.personalCar ?? 0) + (trip.taxi ?? 0) + (trip.other ?? 0))}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                {trip.otherNote && <div className="text-muted small mt-2"><em>Other note:</em> {trip.otherNote}</div>}
                            </CardBody>
                        </Card>

                        {isApprovable && (
                            <Card className="mt-3">
                                <CardBody>
                                    <h5 className="mb-3">Your decision</h5>
                                    <Label>Comment (optional)</Label>
                                    <Input type="textarea" rows={3} value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        disabled={busy} />
                                    <div className="mt-3 d-flex gap-2">
                                        <Button color="success" disabled={busy} onClick={() => act("approve")}>
                                            {busy ? <Spinner size="sm" /> : "Approve"}
                                        </Button>
                                        <Button color="danger" outline disabled={busy} onClick={() => act("reject")}>
                                            Reject
                                        </Button>
                                        <Button color="link" onClick={() => navigate("/travel-expense")}>Cancel</Button>
                                    </div>
                                </CardBody>
                            </Card>
                        )}
                    </Col>

                    <Col xl={4}>
                        <Card>
                            <CardBody>
                                <h5 className="mb-3">Approval history</h5>
                                {log.length === 0 ? (
                                    <div className="text-muted">No approvals or comments yet.</div>
                                ) : (
                                    <ul className="list-unstyled mb-0">
                                        {log.map(l => (
                                            <li key={l.id} className="mb-2 pb-2 border-bottom">
                                                <div>
                                                    <Badge color={l.status === "APPROVED" ? "success" : l.status === "DENIED" ? "danger" : "secondary"} pill>{l.status}</Badge>
                                                    {" "}<small className="text-muted">empId {l.empid}</small>
                                                </div>
                                                <div className="text-muted small">{fmtDt(l.stamp)}</div>
                                                {l.comment && <div className="mt-1 small">{l.comment}</div>}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default TravelExpenseDetail;
